package com.appsyde.shared.presentation.screen.screen

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.collectAsState
import kotlinx.coroutines.delay
import kotlinx.browser.window
import com.appsyde.shared.app.presentation.account.AccountViewModel
import com.appsyde.shared.app.presentation.team.TeamViewModel
import com.appsyde.shared.presentation.style.style.AppStylesAccount
import com.appsyde.shared.presentation.style.style.AppStylesCards
import com.appsyde.shared.presentation.style.style.AppStylesGlobal
import com.appsyde.shared.presentation.style.style.AppStylesHeader
import com.appsyde.shared.presentation.style.style.AppStylesLayout
import com.appsyde.shared.presentation.style.style.AppStylesMessages
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.max
import org.jetbrains.compose.web.attributes.min
import org.jetbrains.compose.web.dom.*
import org.koin.core.context.GlobalContext
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLTextAreaElement
import com.appsyde.shared.modules.account.domain.model.AccountRole

private val FRENCH_MONTHS = listOf(
    "janvier", "février", "mars", "avril", "mai", "juin",
    "juillet", "août", "septembre", "octobre", "novembre", "décembre"
)

/** Formate trialEndsAt (ISO) en "3 avril". */
private fun formatTrialEndDate(trialEndsAt: String?): String? {
    if (trialEndsAt.isNullOrBlank()) return null
    val datePart = trialEndsAt.take(10)
    val parts = datePart.split("-")
    if (parts.size != 3) return null
    val day = parts[2].toIntOrNull() ?: return null
    val month = parts[1].toIntOrNull()?.minus(1)?.takeIf { it in 0..11 } ?: return null
    return "$day ${FRENCH_MONTHS[month]}"
}

private enum class AccountPage { PROFIL, CONFIGURATION, ABONNEMENT, EQUIPE }

@Composable
fun AccountScreen(onLogout: () -> Unit) {
    val viewModel = remember { GlobalContext.get().get<AccountViewModel>() }
    val state by viewModel.state.collectAsState()
    val isOwner = state.role == AccountRole.OWNER
    val teamViewModel = remember { GlobalContext.get().get<TeamViewModel>() }
    val teamState by teamViewModel.state.collectAsState()

    LaunchedEffect(Unit) { viewModel.loadAccount() }
    var selectedPage by remember { mutableStateOf(AccountPage.PROFIL) }
    LaunchedEffect(selectedPage) {
        when (selectedPage) {
            AccountPage.EQUIPE -> teamViewModel.load()
            AccountPage.ABONNEMENT -> viewModel.loadBillingStatus()
            else -> {}
        }
    }
    LaunchedEffect(isOwner, selectedPage) {
        if (!isOwner && (selectedPage == AccountPage.CONFIGURATION || selectedPage == AccountPage.ABONNEMENT)) {
            selectedPage = AccountPage.PROFIL
        }
    }
    var showDeleteAccountModal by remember { mutableStateOf(false) }
    LaunchedEffect(state.deleteRequestMessage) {
        if (state.deleteRequestMessage != null) showDeleteAccountModal = false
    }

    // Même grille que Respond : 280px | 1fr | 300px. Colonne droite vide.
    Div(attrs = { classes(AppStylesLayout.container) }) {
        Div(attrs = { classes(AppStylesLayout.sidebar) }) {
            Div(attrs = { classes(AppStylesCards.ticketCardGlass, AppStylesAccount.accountNavCardWrap) }) {
                AccountNavMinimal(
                    selectedPage = selectedPage,
                    onSelectPage = { selectedPage = it },
                    isOwner = isOwner
                )
            }
        }
        Div(attrs = { classes(AppStylesLayout.mainColumn) }) {
            Div(attrs = { classes(AppStylesCards.pageCardGlass, AppStylesAccount.accountCentralCardWrap) }) {
                Div(attrs = { classes(AppStylesCards.pageCardBody, AppStylesAccount.accountContentWrap) }) {
                    when (selectedPage) {
                        AccountPage.PROFIL -> AccountContentProfil(
                            userName = state.userName,
                            userEmail = state.userEmail,
                            accountEmail = state.account?.primaryEmail,
                            role = state.role,
                            memberCount = if (isOwner && !teamState.isLoading) teamState.users.size else null,
                            onDeleteClick = { showDeleteAccountModal = true },
                            onLogout = onLogout
                        )
                        AccountPage.CONFIGURATION -> AccountContentConfiguration(
                            toneIndex = state.toneIndex,
                            setToneIndex = { viewModel.setToneIndex(it) },
                            signatureEnabled = state.signatureEnabled,
                            setSignatureEnabled = { viewModel.setSignature(it, state.signatureValue) },
                            signatureText = state.signatureValue,
                            setSignatureText = { viewModel.setSignatureText(it) }
                        )
                        AccountPage.ABONNEMENT -> AccountContentAbonnement(
                            billingStatus = state.billingStatus,
                            billingLoading = state.billingLoading,
                            checkoutInProgress = state.checkoutInProgress,
                            portalInProgress = state.portalInProgress,
                            accountEmail = state.account?.primaryEmail,
                            memberCount = if (isOwner && !teamState.isLoading) teamState.users.size else null,
                            viewModel = viewModel
                        )
                        AccountPage.EQUIPE -> AccountContentEquipe(
                            teamViewModel = teamViewModel,
                            teamState = teamState,
                            isOwner = isOwner
                        )
                    }
                }
            }
        }
        Div(attrs = {}) {}
    }
    if (showDeleteAccountModal) {
        DeleteAccountModal(
            onClose = { showDeleteAccountModal = false; viewModel.clearDeleteRequestMessage() },
            onConfirmSendEmail = { viewModel.requestAccountDeletion() },
            isLoading = state.deleteRequestInProgress
        )
    }
}

@Composable
private fun AccountNavMinimal(
    selectedPage: AccountPage,
    onSelectPage: (AccountPage) -> Unit,
    isOwner: Boolean
) {
    Div(attrs = { classes(AppStylesAccount.accountNavMinimal) }) {
        Span(attrs = { classes(AppStylesAccount.accountNavSectionLabel, AppStylesAccount.accountNavSectionLabelFirst) }) {
            Text("Paramètres")
        }
        Button(attrs = {
            classes(listOfNotNull(AppStylesAccount.accountNavItem, if (selectedPage == AccountPage.PROFIL) AppStylesAccount.accountNavItemActive else null))
            attr("type", "button")
            onClick { onSelectPage(AccountPage.PROFIL) }
        }) { Text("Profil") }
        if (isOwner) {
            Button(attrs = {
                classes(listOfNotNull(AppStylesAccount.accountNavItem, if (selectedPage == AccountPage.CONFIGURATION) AppStylesAccount.accountNavItemActive else null))
                attr("type", "button")
                onClick { onSelectPage(AccountPage.CONFIGURATION) }
            }) { Text("Configuration") }
            Button(attrs = {
                classes(listOfNotNull(AppStylesAccount.accountNavItem, if (selectedPage == AccountPage.ABONNEMENT) AppStylesAccount.accountNavItemActive else null))
                attr("type", "button")
                onClick { onSelectPage(AccountPage.ABONNEMENT) }
            }) { Text("Abonnement") }
        }
        Span(attrs = { classes(AppStylesAccount.accountNavSectionLabel) }) { Text("Équipe") }
        Button(attrs = {
            classes(listOfNotNull(AppStylesAccount.accountNavItem, if (selectedPage == AccountPage.EQUIPE) AppStylesAccount.accountNavItemActive else null))
            attr("type", "button")
            onClick { onSelectPage(AccountPage.EQUIPE) }
        }) { Text("Membres") }
    }
}

/** "Alice Dupont" -> "AD", "Alice" -> "A" */
private fun initialsFromDisplayName(displayName: String): String {
    val parts = displayName.trim().split(" ").filter { it.isNotBlank() }
    return when {
        parts.isEmpty() -> "?"
        parts.size == 1 -> parts[0].firstOrNull()?.uppercaseChar()?.toString() ?: "?"
        else -> "${parts[0].first().uppercaseChar()}${parts[1].first().uppercaseChar()}"
    }
}

private fun roleDisplayName(role: String?): String = when (role?.uppercase()) {
    AccountRole.OWNER -> "Propriétaire"
    "MEMBER" -> "Membre"
    else -> role ?: "—"
}

/** Dérive un libellé court pour le workspace à partir de l’email (ex. appsyde.tb.help@gmail.com → AppSyde TB). */
private fun workspaceDisplayName(primaryEmail: String?): String {
    if (primaryEmail.isNullOrBlank()) return "Ce workspace"
    val local = primaryEmail.substringBefore("@")
    val parts = local.split(".").take(2)
    if (parts.isEmpty()) return "Ce workspace"
    return parts.joinToString(" ") { it.replaceFirstChar { c -> c.uppercase() } }
}

@Composable
private fun AccountContentProfil(
    userName: String?,
    userEmail: String?,
    accountEmail: String?,
    role: String?,
    memberCount: Int?,
    onDeleteClick: () -> Unit,
    onLogout: () -> Unit
) {
    val displayName = userName?.takeIf { it.isNotBlank() } ?: userEmail?.substringBefore("@") ?: "—"
    val workspaceName = workspaceDisplayName(accountEmail)
    val isOwner = role?.uppercase() == AccountRole.OWNER

    // Titre de page + sous-texte global + divider
    Div(attrs = { classes(AppStylesAccount.accountHeroLeft) }) {
        Div(attrs = { classes(AppStylesAccount.accountHeroTitle) }) { Text("Profil") }
        Div(attrs = { classes(AppStylesAccount.profilePageSubtitle) }) {
            Text("Gérez votre identité et votre accès au workspace.")
        }
    }
    Div(attrs = { classes(AppStylesAccount.profileHeaderDivider) }) {}

    // 1️⃣ Header incarné (identité) : cercle initiales + nom, badge rôle à droite
    val initials = initialsFromDisplayName(displayName)
    Div(attrs = { classes(AppStylesAccount.profileIdentityBlock) }) {
        Div(attrs = { classes(AppStylesAccount.profileIdentityNameRow) }) {
            Div(attrs = { classes(AppStylesAccount.profileIdentityNameWrap) }) {
                Div(attrs = { classes(AppStylesAccount.profileAvatarCircle) }) {
                    Span(attrs = { classes(AppStylesAccount.profileAvatarCircleText) }) { Text(initials) }
                }
                Div(attrs = { classes(AppStylesAccount.profileIdentityName) }) { Text(displayName) }
            }
            Span(attrs = { classes(AppStylesAccount.profileRoleBadge) }) {
                Text(roleDisplayName(role))
            }
        }
        Div(attrs = { classes(AppStylesAccount.profileIdentityRoleLine) }) {
            Text(
                if (isOwner) "Propriétaire du workspace $workspaceName"
                else "Membre du workspace $workspaceName"
            )
        }
        Div(attrs = { classes(AppStylesAccount.profileIdentitySubtext) }) {
            Text(
                if (!accountEmail.isNullOrBlank())
                    "Vous utilisez la boîte $accountEmail pour répondre aux clients."
                else
                    "Aucune boîte Gmail connectée."
            )
        }
    }

    // 2️⃣ Section « Votre rôle » : titre + description
    Div(attrs = { classes(AppStylesAccount.profileSection) }) {
        Div(attrs = { classes(AppStylesAccount.profileSectionTitle) }) { Text("Votre rôle") }
        Div(attrs = { classes(AppStylesAccount.profileSectionDescription) }) {
            Text(
                if (isOwner)
                    "Vous êtes propriétaire de ce workspace. Vous pouvez inviter des membres, gérer les rôles et traiter les tickets."
                else
                    "Vous êtes membre de ce workspace. Vous pouvez traiter les tickets et rédiger des réponses."
            )
        }
    }

    // 3️⃣ Section « Boîte connectée » (mini carte)
    Div(attrs = { classes(AppStylesAccount.profileSection) }) {
        Div(attrs = { classes(AppStylesAccount.profileSectionTitle) }) { Text("Boîte Gmail connectée") }
        Div(attrs = { classes(AppStylesAccount.profileMailboxCard) }) {
            Div(attrs = { classes(AppStylesAccount.profileMailboxEmail) }) {
                Text(accountEmail ?: "—")
            }
            Div(attrs = { classes(AppStylesAccount.profileMailboxHint) }) {
                Text(
                    when {
                        memberCount != null && memberCount > 0 ->
                            "Cette boîte est utilisée par $memberCount membre${if (memberCount > 1) "s" else ""}."
                        else ->
                            "Cette boîte est utilisée pour répondre aux clients."
                    }
                )
            }
        }
    }

    // 4️⃣ Section « Sécurité » (zone sensible ; supprimer compte réservé au propriétaire)
    Div(attrs = { classes(AppStylesAccount.profileSecuritySection) }) {
        Div(attrs = { classes(AppStylesAccount.profileSecurityTitle) }) { Text("Sécurité") }
        Div(attrs = { classes(AppStylesAccount.profileSecurityActions) }) {
            Button(attrs = {
                classes(AppStylesAccount.profileSecurityLogout)
                attr("type", "button")
                onClick { onLogout() }
            }) { Text("Déconnexion") }
            if (isOwner) {
                Button(attrs = {
                    classes(AppStylesAccount.profileSecurityDelete)
                    attr("type", "button")
                    onClick { onDeleteClick() }
                }) { Text("Supprimer mon compte") }
            }
        }
    }
}

@Composable
private fun AccountContentConfiguration(
    toneIndex: Int,
    setToneIndex: (Int) -> Unit,
    signatureEnabled: Boolean,
    setSignatureEnabled: (Boolean) -> Unit,
    signatureText: String,
    setSignatureText: (String) -> Unit
) {
    val tones = listOf("Professionnel", "Équilibré", "Chaleureux")

    // Header incarné
    Div(attrs = { classes(AppStylesAccount.accountHeroLeft) }) {
        Div(attrs = { classes(AppStylesAccount.accountHeroTitle) }) { Text("Configuration") }
        Div(attrs = { classes(AppStylesAccount.configPageSubtitle) }) {
            Text("Personnalisez la manière dont AppSyde rédige vos réponses.")
        }
    }
    Div(attrs = { classes(AppStylesAccount.configHeaderDivider) }) {}

    // Bloc 1 — Ton IA
    Div(attrs = { classes(AppStylesAccount.configBlock) }) {
        Div(attrs = { classes(AppStylesAccount.configBlockTitle, AppStylesAccount.configBlockTitleFirst) }) { Text("Ton des réponses") }
        Div(attrs = { classes(AppStylesAccount.configBlockDescription) }) {
            Text("Définissez le style général des réponses générées par l'IA.")
        }
        Input(InputType.Range, attrs = {
            id("tone-ia-slider")
            classes(AppStylesAccount.toneSliderPremium)
            min("0")
            max("2")
            attr("step", "1")
            value(toneIndex.toString())
            onInput {
                val v = (it.target as? HTMLInputElement)?.value?.toIntOrNull() ?: 1
                setToneIndex(v.coerceIn(0, 2))
            }
        })
        Div(attrs = { classes(AppStylesAccount.configToneLabels) }) {
            tones.forEachIndexed { idx, label ->
                Button(attrs = {
                    classes(listOfNotNull(AppStylesAccount.configToneLegendLabel, if (idx == toneIndex) AppStylesAccount.configToneLegendLabelActive else null))
                    attr("type", "button")
                    onClick { setToneIndex(idx.coerceIn(0, 2)) }
                }) { Text(label) }
            }
        }
        Div(attrs = { classes(AppStylesAccount.configToneCurrentRow) }) {
            Span(attrs = { classes(AppStylesAccount.configToneCurrent) }) {
                Text("Ton actuel : ${tones[toneIndex.coerceIn(0, 2)]}")
            }
        }
    }

    // Bloc 2 — Signature automatique (titre + toggle alignés, puis description, puis card)
    Div(attrs = { classes(AppStylesAccount.configBlock, AppStylesAccount.configBlockWithTopSpacer) }) {
        Div(attrs = { classes(AppStylesAccount.configBlockTitleRow, AppStylesAccount.configBlockTitleRowFirst) }) {
            Span(attrs = { classes(AppStylesAccount.configBlockTitleInRow) }) { Text("Signature automatique") }
            Button(attrs = {
                classes(listOfNotNull(AppStylesAccount.signatureToggle, if (signatureEnabled) AppStylesAccount.signatureToggleOn else null))
                attr("type", "button")
                onClick { setSignatureEnabled(!signatureEnabled) }
            }) {
                Div(attrs = {
                    classes(listOfNotNull(AppStylesAccount.signatureToggleThumb, if (signatureEnabled) AppStylesAccount.signatureToggleThumbOn else null))
                })
            }
        }
        Div(attrs = { classes(AppStylesAccount.configBlockDescription) }) {
            Text("Ajoutez une signature à la fin de chaque réponse envoyée.")
        }
        Div(attrs = { classes(AppStylesAccount.configSignatureCard) }) {
            Div(attrs = {
                classes(listOfNotNull(AppStylesMessages.bubbleDraft, if (!signatureEnabled) AppStylesAccount.signatureInputDisabled else null))
            }) {
                TextArea(value = signatureText, attrs = {
                    classes(AppStylesAccount.signatureInput, AppStylesAccount.accountTextareaCompact)
                    attr("rows", "3")
                    if (!signatureEnabled) attr("disabled", "true")
                    attr("placeholder", "Ex: Cordialement, L'équipe support")
                    onInput { setSignatureText((it.target as? HTMLTextAreaElement)?.value.orEmpty()) }
                })
            }
        }
    }
}

@Composable
private fun AccountContentAbonnement(
    billingStatus: com.appsyde.shared.modules.account.domain.model.BillingStatus?,
    billingLoading: Boolean,
    checkoutInProgress: Boolean,
    portalInProgress: Boolean,
    accountEmail: String?,
    memberCount: Int?,
    viewModel: AccountViewModel
) {
    var checkoutTrigger by remember { mutableStateOf(0) }
    var portalTrigger by remember { mutableStateOf(0) }
    LaunchedEffect(checkoutTrigger) {
        if (checkoutTrigger > 0) {
            val url = viewModel.startCheckout()
            url?.let { window.location.href = it }
        }
    }
    LaunchedEffect(portalTrigger) {
        if (portalTrigger > 0) {
            val url = viewModel.startCustomerPortal()
            url?.let { window.location.href = it }
        }
    }

    // Header commun (comme Profil / Configuration)
    Div(attrs = { classes(AppStylesAccount.accountHeroLeft) }) {
        Div(attrs = { classes(AppStylesAccount.accountHeroTitle) }) { Text("Abonnement") }
        Div(attrs = { classes(AppStylesAccount.profilePageSubtitle) }) {
            Text("Gérez votre plan AppSyde et votre facturation.")
        }
    }
    Div(attrs = { classes(AppStylesAccount.profileHeaderDivider) }) {}

    when {
        billingLoading -> {
            Div(attrs = { classes(AppStylesAccount.accountSection) }) {
                Div(attrs = { classes(AppStylesAccount.accountAboCard) }) {
                    Span(attrs = { classes(AppStylesAccount.accountAboStatusLine) }) { Text("Chargement…") }
                }
            }
        }
        billingStatus == null -> {
            Div(attrs = { classes(AppStylesAccount.accountSection) }) {
                Div(attrs = { classes(AppStylesAccount.accountAboCard) }) {
                    Span(attrs = { classes(AppStylesAccount.accountAboStatusLine) }) { Text("Impossible de charger le statut d'abonnement.") }
                }
            }
        }
        billingStatus.billingStatus == "trialing" || billingStatus.billingStatus == "past_due" -> {
            // Page activation : Hero → (Essai en cours si trialing) → Plan → Valeur → Workspace → FAQ → Paiement
            val isTrialingWithAccess = billingStatus.billingStatus == "trialing" && billingStatus.hasBillingAccess
            AbonnementActivationContent(
                accountEmail = accountEmail,
                memberCount = memberCount,
                checkoutInProgress = checkoutInProgress,
                onCheckout = { checkoutTrigger++ },
                trialingWithAccess = isTrialingWithAccess,
                daysRemainingTrial = billingStatus.daysRemainingTrial,
                trialEndsAt = billingStatus.trialEndsAt
            )
        }
        billingStatus.billingStatus == "active" || billingStatus.billingStatus == "cancel_at_period_end" -> {
            // Abonnement actif + Résilier
            Div(attrs = { classes(AppStylesAccount.accountSection) }) {
                Div(attrs = { classes(AppStylesAccount.accountSectionContent) }) {
                    Div(attrs = { classes(AppStylesAccount.accountHeroLeft) }) {
                        Div(attrs = { classes(AppStylesAccount.accountHeroTitle) }) { Text("Abonnement actif") }
                    }
                    Div(attrs = { classes(AppStylesAccount.accountAboCard) }) {
                        Div(attrs = { classes(AppStylesAccount.accountAboPlanRow) }) {
                            Span(attrs = { classes(AppStylesAccount.accountAboPlanLabel) }) { Text("Plan") }
                            Span(attrs = { classes(AppStylesAccount.accountAboPlanValue) }) { Text("AppSyde Starter") }
                        }
                        Div(attrs = { classes(AppStylesAccount.accountAboPlanRow) }) {
                            Span(attrs = { classes(AppStylesAccount.accountAboPlanLabel) }) { Text("Facturation") }
                            Span(attrs = { classes(AppStylesAccount.accountAboPlanValue) }) { Text("99 € / mois") }
                        }
                        Div(attrs = { classes(AppStylesAccount.accountAboStatusLine) }) {
                            Text("Votre abonnement est actif.")
                        }
                    }
                    Div(attrs = { classes(AppStylesAccount.profileSecuritySection) }) {
                        Div(attrs = { classes(AppStylesAccount.profileSecurityTitle) }) { Text("Résilier l'abonnement") }
                        Div(attrs = { classes(AppStylesAccount.profileSecurityActions) }) {
                            Button(attrs = {
                                classes(AppStylesAccount.profileSecurityDelete)
                                attr("type", "button")
                                if (portalInProgress) attr("disabled", "true")
                                onClick { portalTrigger++ }
                            }) { Text(if (portalInProgress) "Redirection…" else "Résilier l'abonnement") }
                        }
                    }
                }
            }
        }
        else -> {
            // past_due déjà géré au-dessus ; autre cas (inconnu) → même page activation
            AbonnementActivationContent(
                accountEmail = accountEmail,
                memberCount = memberCount,
                checkoutInProgress = checkoutInProgress,
                onCheckout = { checkoutTrigger++ },
                trialingWithAccess = false,
                daysRemainingTrial = null,
                trialEndsAt = null
            )
        }
    }
}

@Composable
private fun AbonnementActivationContent(
    accountEmail: String?,
    memberCount: Int?,
    checkoutInProgress: Boolean,
    onCheckout: () -> Unit,
    trialingWithAccess: Boolean = false,
    daysRemainingTrial: Int? = null,
    trialEndsAt: String? = null
) {
    Div(attrs = { classes(AppStylesAccount.aboActivationPage) }) {
        // 1️⃣ Hero
        Div(attrs = { classes(AppStylesAccount.aboActivationHeroTitle) }) { Text("Activer AppSyde") }
        Div(attrs = { classes(AppStylesAccount.aboActivationHeroSub) }) {
            Text("Répondez plus vite aux clients avec l'assistance IA.")
        }

        // Essai gratuit en cours (quand trialing avec accès)
        if (trialingWithAccess) {
            Div(attrs = { classes(AppStylesAccount.aboActivationTrialBanner) }) {
                Div(attrs = { classes(AppStylesAccount.aboActivationTrialLabel) }) { Text("Essai gratuit en cours") }
                val days = daysRemainingTrial ?: 0
                val untilDate = formatTrialEndDate(trialEndsAt)
                if (days <= 3 && days >= 0) {
                    Div(attrs = { classes(AppStylesAccount.aboActivationTrialDaysWarning) }) {
                        Text("⚠ Votre essai se termine dans $days jour${if (days <= 1) "" else "s"}${untilDate?.let { " (jusqu'au $it)" } ?: ""}.")
                    }
                } else {
                    Div(attrs = { classes(AppStylesAccount.aboActivationTrialDays) }) {
                        Text("Il vous reste $days jours${untilDate?.let { " (jusqu'au $it)" } ?: ""}.")
                    }
                }
            }
        }

        // 2️⃣ Carte plan
        Div(attrs = { classes(AppStylesAccount.aboActivationPlanCard) }) {
            Div(attrs = { classes(AppStylesAccount.aboActivationPlanCardHeader) }) {
                Div(attrs = { classes(AppStylesAccount.aboActivationPlanCardTitleBlock) }) {
                    Span(attrs = { classes(AppStylesAccount.aboActivationPlanCardTitle) }) { Text("AppSyde") }
                    Span(attrs = { classes(AppStylesAccount.aboActivationPlanCardBadge) }) { Text("Plan équipe") }
                }
                Div(attrs = { classes(AppStylesAccount.aboActivationPlanCardPriceBlock) }) {
                    Span(attrs = { classes(AppStylesAccount.aboActivationPlanCardPriceBig) }) { Text("99 €") }
                    Span(attrs = { classes(AppStylesAccount.aboActivationPlanCardPricePeriod) }) { Text("/ mois") }
                    Div(attrs = { classes(AppStylesAccount.aboActivationPlanCardSub) }) { Text("Jusqu'à 5 agents") }
                }
            }
            Div(attrs = { classes(AppStylesAccount.aboActivationPlanFeatures) }) {
                listOf(
                    "Boîte Gmail connectée",
                    "Copilote IA pour les réponses",
                    "Contexte automatique des tickets",
                    "Collaboration multi-agents",
                    "Historique intelligent des échanges"
                ).forEach { feature ->
                    Div(attrs = { classes(AppStylesAccount.aboActivationPlanFeatureItem) }) {
                        Text("✓ $feature")
                    }
                }
            }
            Button(attrs = {
                classes(AppStylesAccount.aboActivationPlanCta)
                attr("type", "button")
                if (checkoutInProgress) attr("disabled", "true")
                onClick { onCheckout() }
            }) { Text(if (checkoutInProgress) "Redirection…" else "Activer AppSyde") }
            Div(attrs = { classes(AppStylesAccount.aboActivationPlanCtaSub) }) {
                Text(
                    if (trialingWithAccess)
                        "Votre abonnement commencera après la période d'essai. Paiement sécurisé par Stripe. Annulation à tout moment."
                    else
                        "Paiement sécurisé par Stripe. Annulation à tout moment."
                )
            }
        }

        // 3️⃣ Bloc valeur
        Div(attrs = { classes(AppStylesAccount.aboActivationValueSection) }) {
            Div(attrs = { classes(AppStylesAccount.aboActivationValueTitle) }) { Text("Pourquoi utiliser AppSyde ?") }
            Div(attrs = { classes(AppStylesAccount.aboActivationValueCards) }) {
                Div(attrs = { classes(AppStylesAccount.aboActivationValueCard) }) {
                    Div(attrs = { classes(AppStylesAccount.aboActivationValueCardTitle) }) { Text("Répondez plus vite") }
                    Div(attrs = { classes(AppStylesAccount.aboActivationValueCardText) }) {
                        Text("L'IA prépare un brouillon de réponse et vous fournit immédiatement le contexte du ticket.")
                    }
                }
                Div(attrs = { classes(AppStylesAccount.aboActivationValueCard) }) {
                    Div(attrs = { classes(AppStylesAccount.aboActivationValueCardTitle) }) { Text("Réduisez les erreurs") }
                    Div(attrs = { classes(AppStylesAccount.aboActivationValueCardText) }) {
                        Text("Les informations manquantes et les points à vérifier sont détectés automatiquement.")
                    }
                }
                Div(attrs = { classes(AppStylesAccount.aboActivationValueCard) }) {
                    Div(attrs = { classes(AppStylesAccount.aboActivationValueCardTitle) }) { Text("Construisez une mémoire du support") }
                    Div(attrs = { classes(AppStylesAccount.aboActivationValueCardText) }) {
                        Text("Les réponses réellement envoyées améliorent progressivement la qualité des suggestions.")
                    }
                }
            }
        }

        // 4️⃣ Bloc workspace à activer
        Div(attrs = { classes(AppStylesAccount.aboActivationWorkspaceSection) }) {
            Div(attrs = { classes(AppStylesAccount.aboActivationWorkspaceTitle) }) { Text("Workspace à activer") }
            Div(attrs = { classes(AppStylesAccount.aboActivationWorkspaceCard) }) {
                Div(attrs = { classes(AppStylesAccount.aboActivationWorkspaceRow) }) {
                    Text("Workspace : ${workspaceDisplayName(accountEmail)}")
                }
                Div(attrs = { classes(AppStylesAccount.aboActivationWorkspaceRow) }) {
                    Text("Boîte Gmail : ${accountEmail ?: "—"}")
                }
                Div(attrs = { classes(AppStylesAccount.aboActivationWorkspaceRow) }) {
                    Text("Agents : ${memberCount ?: "—"}")
                }
            }
        }

        // 5️⃣ FAQ (aérée : border-top + espace entre blocs)
        Div(attrs = { classes(AppStylesAccount.aboActivationFaqSection) }) {
            Div(attrs = { classes(AppStylesAccount.aboActivationFaqTitle) }) { Text("Questions fréquentes") }
            Div(attrs = { classes(AppStylesAccount.aboActivationFaqList) }) {
                Div(attrs = { classes(AppStylesAccount.aboActivationFaqItem, AppStylesAccount.aboActivationFaqItemFirst) }) {
                    Div(attrs = { classes(AppStylesAccount.aboActivationFaqQuestion) }) { Text("Puis-je annuler mon abonnement ?") }
                    Div(attrs = { classes(AppStylesAccount.aboActivationFaqAnswer) }) {
                        Text("Oui, l'abonnement peut être interrompu à tout moment depuis les paramètres.")
                    }
                }
                Div(attrs = { classes(AppStylesAccount.aboActivationFaqItem) }) {
                    Div(attrs = { classes(AppStylesAccount.aboActivationFaqQuestion) }) { Text("Mes emails restent-ils privés ?") }
                    Div(attrs = { classes(AppStylesAccount.aboActivationFaqAnswer) }) {
                        Text("Oui. Les données sont utilisées uniquement pour générer les réponses et ne servent pas à entraîner les modèles.")
                    }
                }
                Div(attrs = { classes(AppStylesAccount.aboActivationFaqItem) }) {
                    Div(attrs = { classes(AppStylesAccount.aboActivationFaqQuestion) }) { Text("Puis-je ajouter d'autres agents ?") }
                    Div(attrs = { classes(AppStylesAccount.aboActivationFaqAnswer) }) {
                        Text("Le plan inclut jusqu'à 5 agents sur une boîte Gmail partagée.")
                    }
                }
            }
        }

        // 6️⃣ Section Paiement (CTA = même redirection Stripe)
        Div(attrs = { classes(AppStylesAccount.aboActivationPaymentSection) }) {
            Div(attrs = { classes(AppStylesAccount.aboActivationPaymentTitle) }) { Text("Paiement") }
            Div(attrs = { classes(AppStylesAccount.aboActivationPaymentText) }) {
                Text("Votre abonnement sera facturé mensuellement.")
            }
            Button(attrs = {
                classes(AppStylesAccount.aboActivationPlanCta)
                attr("type", "button")
                if (checkoutInProgress) attr("disabled", "true")
                onClick { onCheckout() }
            }) { Text(if (checkoutInProgress) "Redirection…" else "Activer AppSyde") }
        }
    }
}

/** Écran de confirmation après paiement Stripe réussi (même style que page d'acceptation d'invitation : header + card avec ✓). */
@Composable
fun BillingSuccessScreen(onGoToTickets: () -> Unit) {
    Div(attrs = { classes(AppStylesGlobal.appRoot) }) {
        // Header comme InviteAcceptPage : brand + sous-titre "Abonnement"
        Div(attrs = { classes(AppStylesHeader.header) }) {
            Div(attrs = { classes(AppStylesAccount.invitePageHeaderInner) }) {
                Div(attrs = { classes(AppStylesHeader.brandRow) }) {
                    Img("/appsydeIcon.png", attrs = {
                        classes(AppStylesHeader.brandIcon)
                        attr("alt", "AppSyde")
                    })
                    Div(attrs = { classes(AppStylesHeader.brandCol) }) {
                        Div(attrs = { classes(AppStylesHeader.brand) }) { Text("AppSyde") }
                        Span(attrs = { classes(AppStylesHeader.brandSubtitle) }) { Text("Abonnement") }
                    }
                }
                Span(attrs = { classes(AppStylesAccount.invitePageHeaderRight) }) { Text("Confirmation") }
            }
        }
        Div(attrs = { classes(AppStylesGlobal.global, AppStylesLayout.globalContentWrap, AppStylesLayout.wizardPageWrap) }) {
            Div(attrs = { classes(AppStylesLayout.wizardContentWrapCentered) }) {
                Div(attrs = { classes(AppStylesAccount.inviteCardWrap) }) {
                    Div(attrs = { classes(AppStylesAccount.inviteCard) }) {
                        Div(attrs = { classes(AppStylesAccount.inviteCardInner) }) {
                            Div(attrs = { classes(AppStylesAccount.billingSuccessTitleBlock) }) {
                                Div(attrs = { classes(AppStylesAccount.inviteHeroBadge) }) {
                                    Span(attrs = { classes(AppStylesAccount.inviteHeroBadgeIcon) }) { Text("✓") }
                                }
                                Div(attrs = { classes(AppStylesAccount.inviteTitle) }) { Text("Abonnement activé 🎉") }
                            }
                            Div(attrs = { classes(AppStylesAccount.inviteDescription) }) {
                                Text("AppSyde est maintenant actif pour votre équipe.")
                            }
                            Div(attrs = { classes(AppStylesAccount.inviteDescription) }) {
                                Text("Vous pouvez commencer à traiter vos tickets immédiatement.")
                            }
                            Div(attrs = { classes(AppStylesAccount.inviteActionWrap, AppStylesAccount.billingSuccessCtaWrap) }) {
                                Button(attrs = {
                                    classes(AppStylesAccount.inviteBtnPrimary)
                                    attr("type", "button")
                                    onClick { onGoToTickets() }
                                }) { Text("Aller aux tickets") }
                            }
                            Div(attrs = { classes(AppStylesAccount.billingSuccessRedirectHint) }) {
                                Text("Redirection automatique vers vos tickets…")
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AccountContentEquipe(
    teamViewModel: TeamViewModel,
    teamState: com.appsyde.shared.app.presentation.team.TeamViewModel.State,
    isOwner: Boolean
) {
    var inviteEmail by remember { mutableStateOf("") }
    LaunchedEffect(teamState.createInviteMessage) {
        if (teamState.createInviteMessage != null && !teamState.createInviteError) inviteEmail = ""
    }
    val pendingInvites = teamState.invites.filter { it.usedAt == null && it.revokedAt == null }
    val activeUsers = teamState.users.filter { it.status.equals("ACTIVE", ignoreCase = true) }
    val memberCount = activeUsers.size
    val pendingCount = pendingInvites.size

    // Header incarné
    Div(attrs = { classes(AppStylesAccount.accountHeroLeft) }) {
        Div(attrs = { classes(AppStylesAccount.accountHeroTitle) }) { Text("Équipe") }
        Div(attrs = { classes(AppStylesAccount.teamPageSubtitle) }) {
            Text("Gérez les membres du workspace et leurs accès.")
        }
    }
    Div(attrs = { classes(AppStylesAccount.teamHeaderDivider) }) {}
    Div(attrs = { classes(AppStylesAccount.teamStatsLine) }) {
        Text(
            "$memberCount membre${if (memberCount != 1) "s" else ""} • $pendingCount invitation${if (pendingCount != 1) "s" else ""} en attente"
        )
    }

    // Bloc 1 — Inviter un membre (réservé aux owners)
    if (isOwner) Div(attrs = { classes(AppStylesAccount.teamBlock) }) {
        Div(attrs = { classes(AppStylesAccount.teamBlockTitle, AppStylesAccount.teamBlockTitleFirst) }) { Text("Inviter un membre") }
        Div(attrs = { classes(AppStylesAccount.teamBlockDescription) }) {
            Text("Ajoutez une personne à ce workspace.")
        }
        Div(attrs = { classes(AppStylesAccount.teamInviteRow) }) {
            Input(InputType.Email, attrs = {
                classes(AppStylesAccount.teamInviteInput)
                value(inviteEmail)
                onInput { inviteEmail = (it.target as? HTMLInputElement)?.value.orEmpty() }
                attr("placeholder", "email@exemple.com")
            })
            Button(attrs = {
                classes(AppStylesAccount.teamInviteBtn)
                attr("type", "button")
                if (teamState.createInviteLoading) attr("disabled", "true")
                onClick { teamViewModel.createInvite(inviteEmail) }
            }) { Text(if (teamState.createInviteLoading) "Envoi…" else "Envoyer") }
        }
        if (teamState.createInviteMessage != null) {
            Div(attrs = {
                classes(
                    if (teamState.createInviteError) AppStylesAccount.deleteRequestMessage else AppStylesAccount.teamMessageOk
                )
            }) { Text(teamState.createInviteMessage ?: "") }
        } else {
            Div(attrs = { classes(AppStylesAccount.teamInviteHint) }) {
                Text("Une invitation sera envoyée par email.")
            }
        }
    }

    // Bloc 2 — Invitations en attente (Révoquer réservé aux owners)
    Div(attrs = {
        classes(listOfNotNull(AppStylesAccount.teamBlock, if (isOwner) AppStylesAccount.teamBlockWithTopSpacer else null))
    }) {
        Div(attrs = {
            classes(listOfNotNull(AppStylesAccount.teamBlockTitle, if (!isOwner) AppStylesAccount.teamBlockTitleFirst else null))
        }) { Text("Invitations en attente") }
        Div(attrs = { classes(AppStylesAccount.accountTeamList) }) {
            if (teamState.isLoading && teamState.invites.isEmpty() && teamState.users.isEmpty()) {
                Div(attrs = { classes(AppStylesAccount.teamListRow, AppStylesAccount.teamListRowLast) }) {
                    Span(attrs = { classes(AppStylesAccount.teamListEmail) }) { Text("Chargement…") }
                }
            } else if (pendingInvites.isEmpty()) {
                Div(attrs = { classes(AppStylesAccount.teamListRow, AppStylesAccount.teamListRowLast) }) {
                    Span(attrs = { classes(AppStylesAccount.teamListEmail) }) { Text("Aucune invitation en attente") }
                }
            } else {
                pendingInvites.forEachIndexed { index, inv ->
                    val isLast = index == pendingInvites.size - 1
                    Div(attrs = {
                        if (isLast) classes(AppStylesAccount.teamListRow, AppStylesAccount.teamListRowLast)
                        else classes(AppStylesAccount.teamListRow)
                    }) {
                        Span(attrs = { classes(AppStylesAccount.teamListEmail) }) { Text(inv.email) }
                        if (isOwner) Div(attrs = { classes(AppStylesAccount.teamListRight) }) {
                            Button(attrs = {
                                classes(AppStylesAccount.teamRowAction)
                                attr("type", "button")
                                onClick { teamViewModel.revokeInvite(inv.id) }
                            }) { Text("Révoquer") }
                        }
                    }
                }
            }
        }
    }

    // Bloc 3 — Membres du workspace (Retirer réservé aux owners) (uniquement actifs ; les retirés ne s'affichent plus)
    Div(attrs = { classes(AppStylesAccount.teamBlock, AppStylesAccount.teamBlockWithTopSpacer) }) {
        Div(attrs = { classes(AppStylesAccount.teamBlockTitle) }) { Text("Membres du workspace") }
        Div(attrs = { classes(AppStylesAccount.accountTeamList) }) {
            if (teamState.isLoading && teamState.users.isEmpty()) {
                Div(attrs = { classes(AppStylesAccount.teamListRow, AppStylesAccount.teamListRowLast) }) {
                    Span(attrs = { classes(AppStylesAccount.teamListEmail) }) { Text("Chargement…") }
                }
            } else if (activeUsers.isEmpty()) {
                Div(attrs = { classes(AppStylesAccount.teamListRow, AppStylesAccount.teamListRowLast) }) {
                    Span(attrs = { classes(AppStylesAccount.teamListEmail) }) { Text("Aucun membre") }
                }
            } else {
                activeUsers.forEachIndexed { index, u ->
                    val canDisable = isOwner && u.role != AccountRole.OWNER
                    val isLast = index == activeUsers.size - 1
                    Div(attrs = {
                        if (isLast) classes(AppStylesAccount.teamListRow, AppStylesAccount.teamListRowLast)
                        else classes(AppStylesAccount.teamListRow)
                    }) {
                        Span(attrs = { classes(AppStylesAccount.teamListEmail) }) { Text(u.email ?: "") }
                        Div(attrs = { classes(AppStylesAccount.teamListRight) }) {
                            Span(attrs = {
                                classes(listOfNotNull(
                                    AppStylesAccount.teamRoleBadge,
                                    if (u.role == AccountRole.OWNER) AppStylesAccount.teamRoleBadgeOwner else null)
                                )
                            }) { Text(if (u.role == AccountRole.OWNER) "Owner" else "Membre") }
                            if (canDisable) {
                                Button(attrs = {
                                    classes(AppStylesAccount.teamRowAction)
                                    attr("type", "button")
                                    onClick { teamViewModel.disableUser(u.id) }
                                }) { Text("Retirer") }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AccountContentDanger(onRequestDeletion: () -> Unit) {
    Div(attrs = { classes(AppStylesAccount.accountSection, AppStylesAccount.accountDangerZone) }) {
        Div(attrs = { classes(AppStylesAccount.accountDangerZoneLabel) }) { Text("Zone sensible") }
        Button(attrs = {
            classes(AppStylesAccount.accountDangerZoneLink)
            attr("type", "button")
            onClick { _ -> onRequestDeletion() }
        }) { Text("Supprimer mon compte") }
    }
}

// ---------- Legacy: modal suppression + composants réutilisés par d'autres écrans ----------
@Composable
fun AccountSidebar(
    connectedEmail: String? = null,
    onRequestDeletion: () -> Unit = {},
    deleteRequestInProgress: Boolean = false,
    deleteRequestMessage: String? = null,
    onClearDeleteMessage: () -> Unit = {}
) {
    var showDeleteAccountModal by remember { mutableStateOf(false) }
    LaunchedEffect(deleteRequestMessage) {
        if (deleteRequestMessage != null) showDeleteAccountModal = false
    }
    Div {
        Div(attrs = { classes(AppStylesCards.pageCardGlass, AppStylesAccount.accountLateralCard) }) {
            Div(attrs = { classes(AppStylesAccount.accountLateralCardBody, AppStylesAccount.accountSidebar) }) {
                AccountProfilContent(connectedEmail = connectedEmail)
                Div(attrs = { classes(AppStylesAccount.accountSidebarDivider) })
                AccountAbonnementContent()
                if (deleteRequestMessage != null) {
                    Div(attrs = { classes(AppStylesAccount.deleteRequestMessage) }) {
                        Text(deleteRequestMessage ?: "")
                        Button(attrs = {
                            classes(AppStylesAccount.actionGhost)
                            attr("type", "button")
                            onClick { onClearDeleteMessage() }
                        }) { Text("Fermer") }
                    }
                }
                AccountGestionCompteContent(onRequestDeleteAccount = { showDeleteAccountModal = true })
            }
        }
        if (showDeleteAccountModal) {
            DeleteAccountModal(
                onClose = { showDeleteAccountModal = false; onClearDeleteMessage() },
                onConfirmSendEmail = onRequestDeletion,
                isLoading = deleteRequestInProgress
            )
        }
    }
}

@Composable
private fun AccountProfilContent(connectedEmail: String?) {
    Div(attrs = { classes(AppStylesAccount.accountSidebarBlock) }) {
        Div(attrs = { classes(AppStylesAccount.accountSidebarBlockTitle) }) { Text("Profil") }
        Div(attrs = { classes(AppStylesAccount.fieldsGrid) }) {
            FieldRow("Nom d'agent", "Agent AppSyde", isLast = false)
            FieldRow("Compte connecté", connectedEmail ?: "—", isLast = true)
        }
    }
}

@Composable
private fun AccountAbonnementContent() {
    Div(attrs = { classes(AppStylesAccount.accountSidebarBlock) }) {
        Div(attrs = { classes(AppStylesAccount.accountSidebarBlockTitle) }) { Text("Abonnement") }
        Div(attrs = { classes(AppStylesAccount.fieldsGrid) }) {
            Div(attrs = { classes(AppStylesAccount.fieldRow, AppStylesAccount.fieldRowLast) }) {
                Div(attrs = { classes(AppStylesAccount.fieldLabel) }) { Text("Plan actuel") }
                Div(attrs = { classes(AppStylesAccount.fieldValue) }) { Text("Starter") }
            }
        }
        Div(attrs = { classes(AppStylesAccount.actionsRow) }) {
            Button(attrs = { classes(AppStylesAccount.actionGhost); attr("type", "button") }) {
                Text("Gérer l'abonnement")
            }
        }
    }
}

@Composable
private fun AccountGestionCompteContent(onRequestDeleteAccount: () -> Unit = {}) {
    Div(attrs = { classes(AppStylesAccount.accountDangerZoneBlock) }) {
        Div(attrs = { classes(AppStylesAccount.accountDangerZoneTitle) }) { Text("Gestion du compte") }
        Button(attrs = {
            classes(AppStylesAccount.actionDanger)
            attr("type", "button")
            onClick { onRequestDeleteAccount() }
        }) { Text("Supprimer mon compte") }
    }
}

@Composable
fun DeleteAccountModal(
    onClose: () -> Unit,
    onConfirmSendEmail: () -> Unit,
    isLoading: Boolean = false
) {
    Div(attrs = { classes(AppStylesAccount.deleteAccountModalOverlay); onClick { onClose() } }) {
        Div(attrs = { classes(AppStylesAccount.deleteAccountModalDialog); onClick { it.stopPropagation() } }) {
            Div(attrs = { classes(AppStylesAccount.deleteAccountModalTitle) }) {
                Text("Supprimer définitivement votre compte ?")
            }
            Div(attrs = { classes(AppStylesAccount.deleteAccountModalBody) }) {
                Div(attrs = { classes(AppStylesAccount.deleteAccountModalSection) }) {
                    Div(attrs = { classes(AppStylesAccount.deleteAccountModalSubheading) }) { Text("Ce qui sera supprimé") }
                    org.jetbrains.compose.web.dom.Ul(attrs = { classes(AppStylesAccount.deleteAccountModalList) }) {
                        org.jetbrains.compose.web.dom.Li(attrs = { classes(AppStylesAccount.deleteAccountModalListItem) }) {
                            Text("Vos tickets et messages enregistrés dans AppSyde")
                        }
                        org.jetbrains.compose.web.dom.Li(attrs = { classes(AppStylesAccount.deleteAccountModalListItem) }) {
                            Text("Vos paramètres (ton IA, signature, configuration)")
                        }
                        org.jetbrains.compose.web.dom.Li(attrs = { classes(AppStylesAccount.deleteAccountModalListItem) }) {
                            Text("L'accès à votre compte")
                        }
                    }
                }
                Div(attrs = { classes(AppStylesAccount.deleteAccountModalSection) }) {
                    Div(attrs = { classes(AppStylesAccount.deleteAccountModalSubheading) }) { Text("Ce qui ne sera pas supprimé") }
                    org.jetbrains.compose.web.dom.Ul(attrs = { classes(AppStylesAccount.deleteAccountModalList) }) {
                        org.jetbrains.compose.web.dom.Li(attrs = { classes(AppStylesAccount.deleteAccountModalListItem) }) {
                            Text("Vos emails Gmail restent accessibles dans Gmail.")
                        }
                    }
                }
                Div(attrs = { classes(AppStylesAccount.deleteAccountModalParagraph) }) { Text("Cette action est irréversible.") }
                Div(attrs = { classes(AppStylesAccount.deleteAccountModalParagraph) }) {
                    Text("Pour des raisons de sécurité, vous devrez confirmer la suppression via un email envoyé à votre adresse connectée.")
                }
            }
            Div(attrs = { classes(AppStylesAccount.deleteAccountModalButtons) }) {
                Button(attrs = {
                    classes(AppStylesAccount.deleteAccountModalButtonSecondary)
                    attr("type", "button")
                    onClick { onClose() }
                }) { Text("Annuler") }
                Button(attrs = {
                    classes(AppStylesAccount.deleteAccountModalButtonPrimary)
                    attr("type", "button")
                    if (isLoading) attr("disabled", "true")
                    onClick { if (!isLoading) onConfirmSendEmail() }
                }) { Text(if (isLoading) "Envoi en cours…" else "Envoyer l'email de confirmation") }
            }
        }
    }
}

@Composable
private fun FieldRow(label: String, value: String, isLast: Boolean = false) {
    Div(attrs = {
        classes(
            listOfNotNull(
                AppStylesAccount.fieldRow,
                if (isLast) AppStylesAccount.fieldRowLast else null
            )
        )
    }) {
        Div(attrs = { classes(AppStylesAccount.fieldLabel) }) { Text(label) }
        Div(attrs = { classes(AppStylesAccount.fieldValue) }) { Text(value) }
    }
}
